<template>
  <el-form
    ref="form"
    label-width="auto"
    size="small"
  >
    <el-form-item
      label="Name"
      :show-message="!opportunity.account.$fields.name.valid"
      :error="opportunity.account.$fields.name.message"
      required
    >
      <el-select
        v-model="opportunity.account"
        filterable
        remote
        default-first-option
        allow-create
        value-key="id"
        class="w-100"
        :remote-method="remoteMethodAccount"
        :loading="isLoadingAccounts"
        @change="handleSelectAccount"
      >
        <el-divider
          v-if="accounts.length > 0"
          class="m-0 p-0"
        >
          Existing Accounts
        </el-divider>

        <el-option
          v-for="item in accounts"
          :key="item.id"
          :label="item.name"
          :value="item"
          :disabled="$_.get(item, 'status') === 'trading'"
        >
          {{ item.name }}
          <el-tag
            type="info"
            size="mini"
            class="ml-2"
          >
            {{ item.status | titleize }}
          </el-tag>
        </el-option>
      </el-select>
    </el-form-item>

    <el-form-item
      label="Address"
      :show-message="!opportunity.account.$fields.address.valid"
      :error="opportunity.account.$fields.address.message"
      required
    >
      <el-input
        v-model="opportunity.account.address"
        clearable
        :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
      />
    </el-form-item>

    <el-row>
      <el-col :span="8">
        <el-form-item
          label="Suburb"
          :show-message="!opportunity.account.$fields.suburb.valid"
          :error="opportunity.account.$fields.suburb.message"
          required
        >
          <el-autocomplete
            v-model="opportunity.account.suburb"
            placement="top"
            clearable
            :fetch-suggestions="findSuburb"
            :trigger-on-focus="false"
            :debounce="500"
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
            @select="handleSuburbSelection"
          >
            <template v-slot="{ item }">
              {{ `${item.suburb}, ${item.state}, ${item.postcode}` }}
            </template>
          </el-autocomplete>
        </el-form-item>
      </el-col>

      <el-col :span="8">
        <el-form-item
          label="State"
          :show-message="!opportunity.account.$fields.state.valid"
          :error="opportunity.account.$fields.state.message"
          required
        >
          <el-input
            v-model="opportunity.account.state"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>

      <el-col :span="8">
        <el-form-item
          label="Postcode"
          :show-message="!opportunity.account.$fields.postcode.valid"
          :error="opportunity.account.$fields.postcode.message"
          required
        >
          <el-input
            v-model="opportunity.account.postcode"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Contact"
          :show-message="!opportunity.account.$fields.contact.valid"
          :error="opportunity.account.$fields.contact.message"
          required
        >
          <el-input
            v-model="opportunity.account.contact"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Phone"
          :show-message="!opportunity.account.$fields.phone.valid"
          :error="opportunity.account.$fields.phone.message"
          required
        >
          <el-input
            v-model="opportunity.account.phone"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Fax"
          :show-message="!opportunity.account.$fields.fax.valid"
          :error="opportunity.account.$fields.fax.message"
        >
          <el-input
            v-model="opportunity.account.fax"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Email"
          :show-message="!opportunity.account.$fields.email.valid"
          :error="opportunity.account.$fields.email.message"
        >
          <el-input
            v-model="opportunity.account.email"
            clearable
            :disabled="!$_.get(opportunity, 'account.name') || !!$_.get(opportunity, 'account.id')"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-form-item
      label="Portfolio"
      :show-message="!opportunity.account.$fields.portfolio.valid"
      :error="opportunity.account.$fields.portfolio.message"
      required
    >
      <el-select
        v-model="opportunity.account.portfolio"
        :loading="isLoadingPortfolios"
        value-key="id"
        class="w-100"
        clearable
      >
        <el-option
          v-for="portfolio in portfolios"
          :key="portfolio.id"
          :label="`${portfolio.long || '-'}`"
          :value="portfolio"
        />
      </el-select>
    </el-form-item>

    <el-form-item
      label="Opportunity Status"
      :show-message="!opportunity.$fields.status.valid"
      :error="opportunity.$fields.status.message"
      required
    >
      <el-select
        v-model="opportunity.status"
        class="w-100"
      >
        <el-option
          v-for="(option, index) in [ 'lead', 'working on', 'awaiting decision' ]"
          :key="index"
          :label="$filters.titleize(option)"
          :value="option"
        />
      </el-select>
    </el-form-item>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-row>
          <el-col>
            <el-form-item
              :show-message="!opportunity.$fields.worth.valid"
              :error="opportunity.$fields.worth.message"
              required
            >
              <template #label>
                Annual Spend ($)
              </template>

              <el-input-number
                v-model="opportunity.worth"
                :precision="0"
                :step="10000"
                :min="0"
                step-strictly
                class="w-100"
              />
            </el-form-item>
          </el-col>

          <el-col>
            <el-form-item
              label="Incumbent Carriers"
              :show-message="!opportunity.$fields.incumbent_carriers.valid"
              :error="opportunity.$fields.incumbent_carriers.message"
              required
            >
              <el-select
                v-model="incumbentCarriers"
                multiple
                filterable
                remote
                value-key="id"
                allow-create
                default-first-option
                :remote-method="findCarrier"
                :loading="isLoadingCarriers"
                class="w-100"
              >
                <el-option
                  v-for="carrier in carriers"
                  :key="carrier.id"
                  :label="carrier.name"
                  :value="carrier"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Daily Volume"
          :show-message="!opportunity.$fields.volume.valid"
          :error="opportunity.$fields.volume.message"
          required
        >
          <el-input
            v-model="opportunity.volume"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 4}"
            clearable
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Suggested Carriers"
          :show-message="!opportunity.$fields.suggested_carriers.valid"
          :error="opportunity.$fields.suggested_carriers.message"
        >
          <el-select
            v-model="suggestedCarriers"
            multiple
            filterable
            remote
            value-key="id"
            default-first-option
            :remote-method="findCarrier"
            :loading="isLoadingCarriers"
            class="w-100"
          >
            <el-option
              v-for="carrier in carriers"
              :key="carrier.id"
              :label="carrier.name"
              :value="carrier"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Freight Types"
          :show-message="!opportunity.$fields.freights.valid"
          :error="opportunity.$fields.freights.message"
          required
        >
          <el-select
            v-model="selectedItemTypes"
            multiple
            value-key="id"
            :loading="isLoadingItemTypes"
            class="w-100"
          >
            <el-option
              v-for="type in itemTypes"
              :key="type.id"
              :label="type.desc"
              :value="type"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <el-form-item
      label="Comments"
      :show-message="!opportunity.$fields.reason.valid"
      :error="opportunity.$fields.reason.message"
      required
    >
      <el-input
        v-model="opportunity.reason"
        type="textarea"
        :autosize="{ minRows: 6, maxRows: 8}"
        clearable
      />
    </el-form-item>
  </el-form>
</template>

<script>
import AccountLead from '@/plugins/sdk/resources/Account/Lead'

export default {
  props: {
    opportunity: {
      type: Object,
      required: true,
      validator: function (value) {
        return value instanceof AccountLead
      }
    }
  },
  data () {
    return {
      isLoadingAccounts: false,
      isLoadingPortfolios: false,
      isLoadingCarriers: false,
      isLoadingItemTypes: false,
      accounts: [],
      carriers: [],
      incumbentCarriers: [],
      suggestedCarriers: [],
      itemTypes: [],
      selectedItemTypes: [],
      portfolios: [],
      rules: {
        'account.name': [
          { required: true, message: 'An Opportunity Name is required', trigger: 'blur' }
        ],
        'account.address': [
          { required: true, message: 'An Address is required', trigger: 'blur' }
        ],
        'account.suburb': [
          { required: true, message: 'Please select a Suburb from the list', trigger: 'change' }
        ],
        'account.state': [
          { required: true, message: 'A State of the Suburb is required', trigger: 'change' }
        ],
        'account.postcode': [
          { required: true, message: 'A Postcode of the Suburb is required', trigger: 'change' }
        ],
        'account.contact': [
          { required: true, message: 'A Contact is required', trigger: 'change' }
        ],
        'account.phone': [
          { required: true, message: 'A Phone Number is required', trigger: 'change' }
        ],
        'account.portfolio': [
          { required: true, type: 'object', message: 'A Portfolio is required', trigger: 'change' }
        ],
        status: [
          { required: true, message: 'An Opportunity Status is required', trigger: 'change' }
        ],
        worth: [
          { required: true, message: 'An Annual Spend is required', trigger: 'blur' }
        ],
        volume: [
          { required: true, message: 'A Daily Volume is required', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    incumbentCarriers (selected) {
      const carriers = []

      selected
        .map(selection => {
          carriers.push(Object.assign(new this.$sdk.account_lead_incumbent_carrier(), {
            name: (typeof selection === 'string' ? selection : undefined),
            carrier: (selection instanceof this.$sdk.carrier ? selection : undefined)
          }))
        })

      this.opportunity.incumbent_carriers = carriers
    },
    suggestedCarriers (selected) {
      const carriers = []

      selected
        .map(selection => {
          carriers.push(Object.assign(new this.$sdk.account_lead_suggested_carrier(), {
            carrier: (selection instanceof this.$sdk.carrier ? selection : undefined)
          }))
        })

      this.opportunity.suggested_carriers = carriers
    },
    selectedItemTypes (selected) {
      const freights = []

      selected
        .map(selection => {
          freights.push(Object.assign(new this.$sdk.account_lead_freight(), {
            type: selection
          }))
        })

      this.opportunity.freights = freights
    }
  },
  created () {
    if (!(this.opportunity.account instanceof this.$sdk.account)) {
      this.opportunity.account = Object.assign(new this.$sdk.account(), {
        status: 'lead'
      })
    }

    this.getItemTypes()
    this.getPortfolios()
  },
  methods: {
    remoteMethodAccount (query) {
      this.isLoadingAccounts = true

      this
        .$sdk
        .account
        .list(builder => {
          builder.filters = { account_name: query, account_active: 1 }
        })
        .then(res => {
          this.accounts = res
        })
        .finally(() => { this.isLoadingAccounts = false })
    },
    handleSelectAccount (selected) {
      if (typeof selected === 'string') {
        this.opportunity.account = Object.assign(new this.$sdk.account(), {
          name: selected,
          status: 'lead'
        })
      } else {
        this.opportunity.account = selected
      }
    },
    getItemTypes () {
      this.isLoadingItemTypes = true

      this
        .$sdk
        .dictionary_itemtype
        .list()
        .then(res => { this.itemTypes.push(...res) })
        .finally(() => { this.isLoadingItemTypes = false })
    },
    getPortfolios () {
      this.isLoadingPortfolios = true

      this
        .$sdk
        .dictionary_portfolio
        .list(request => {
          request.limit = 100
        })
        .then(res => { this.portfolios.push(...res) })
        .finally(() => { this.isLoadingPortfolios = false })
    },
    findCarrier (query) {
      this.isLoadingCarriers = true

      this
        .$sdk
        .carrier
        .list(builder => {
          builder.filters = { carrier_name: query }
          builder.order = 'carrier_name:asc'
        })
        .then(res => { this.carriers = res })
        .finally(() => { this.isLoadingCarriers = false })
    },
    findSuburb (query, cb) {
      this
        .$sdk
        .dictionary_location
        .list(builder => {
          builder.page = 1
          builder.limit = 50
          builder.filters = { dictionary_location_find: [query] }
          builder.order = 'suburb:asc'
        })
        .then(cb)
    },
    handleSuburbSelection (item) {
      this.opportunity.account.suburb = item.suburb
      this.opportunity.account.state = item.state
      this.opportunity.account.postcode = item.postcode
    }
  }
}
</script>
